import * as React from 'react';
import sanitizeHtml from 'sanitize-html';
import isFunction from 'lodash/isFunction';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { isEmail, isRequired } from 'utils/validators';
import { insertIf } from 'utils/list';

import './sov-banner-consumer-insights.scss';

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['strong']
};

applyTranslations(require('./sov-banner-consumer-insights.i18n.json'));

const MSG_KEY = 'sov_banner_consumer_insights';

type Props = {
  onFieldChange: (name: string, value: string) => void,
  onSubmit: () => void
};

class SOVBannerConsumerInsights extends React.PureComponent<Props> {
  handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { onFieldChange } = this.props;
    const target = event.currentTarget;

    if (onFieldChange) {
      onFieldChange(target.name, target.value);
    }
  };

  renderHeadline(): React.ReactNode {
    return (
      <div
        className="sov-banner-consumer-insights__headline"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(__t(`${MSG_KEY}.headline`), SANITIZE_HTML_CONFIG) }}
      />
    );
  }

  renderTextGroup(group: 'one' | 'two' | 'three'): React.ReactNode {
    return (
      <div className="sov-banner-consumer-insights__text-group">
        <div className="sov-banner-consumer-insights__text-group-heading">
          {__t(`${MSG_KEY}.text_group_${group}.headline`)}
        </div>
        <div className="sov-banner-consumer-insights__text-group-body">
          {__t(`${MSG_KEY}.text_group_${group}.body`)}
        </div>
      </div>
    );
  }

  renderImage(): React.ReactNode {
    return (
      <div className="sov-banner-consumer-insights__image-container">
        <img
          src="/static/images/ci-sov-banner-graph.png"
          alt={__t(`${MSG_KEY}.messages.alt_text`)}
          className="sov-banner-consumer-insights__image"
        />
      </div>
    );
  }

  renderFormField(type: 'email' | 'name'): React.ReactNode {
    const value = this.props[type] || '';

    return (
      <FormField
        direction="column"
        label={__t(`${MSG_KEY}.form.field_label_${type}`)}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark
        validators={[
          isRequired(value),
          ...insertIf(type === 'email', ...[
            isEmail(value)
          ])
        ]}
      >
        <Input
          name={type}
          value={value}
          width="responsive"
          onChange={this.handleFieldChange}
        />
      </FormField>
    );
  }

  renderFormFields(): React.ReactNode {
    return (
      <div className="sov-banner-consumer-insights__form-fields">
        {this.renderFormField('email')}
        {this.renderFormField('name')}
      </div>
    );
  }

  renderColumnLeft(): React.ReactNode {
    return (
      <div className="sov-banner-consumer-insights__column-left">
        {this.renderTextGroup('one')}
        {this.renderTextGroup('two')}
        {this.renderTextGroup('three')}
      </div>
    );
  }

  renderColumnRight(): React.ReactNode {
    return (
      <div className="sov-banner-consumer-insights__column-right">
        {this.renderImage()}
        <div className="sov-banner-consumer-insights__form-header">
          {__t(`${MSG_KEY}.form.header`)}
        </div>
        <div className="sov-banner-consumer-insights__form-disclaimer">
          {__t(`${MSG_KEY}.messages.disclaimer`)}
        </div>
        {this.renderFormFields()}
      </div>
    );
  }

  render(): React.ReactNode {
    const { onSubmit } = this.props;

    if (!isFunction(onSubmit)) {
      return null;
    }

    return (
      <div className="sov-banner-consumer-insights">
        <Form
          className="sov-banner-consumer-insights__form"
          onSubmit={onSubmit}
        >
          {this.renderHeadline()}
          <div className="sov-banner-consumer-insights__columns">
            {this.renderColumnLeft()}
            {this.renderColumnRight()}
          </div>
          <div className="sov-banner-consumer-insights__button">
            <ActionButton
              type="submit"
              color="primary"
              width="maximum"
            >
              {__t(`${MSG_KEY}.button.text`)}
            </ActionButton>
          </div>
        </Form>
      </div>
    );
  }
}

export default SOVBannerConsumerInsights;
